import { useEffect, useId, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
} from "react-hook-form";
import { FormInput } from "components/ui/form/FormInput";
import { Select } from "components/ui/select/Select";
import { AutoSuggestion } from "components/ui/auto-suggestion/AutoSuggestion";
import {
  AddressSuggestion,
  InputResponse,
  useGetAddressZipCodeQuery,
} from "pages/application/applicationService";
import { statesOfUSA } from "utilities/statesOfUSA";
import { sort } from "utilities/string";
import "./address-fields-block.scss";

type FormValues = Record<string, string>;

interface AddressFieldsBlockProps {
  /** All address-related inputs for this block (fullAddress, unitType, unitIdentifier, city, state, zipCode). */
  inputs: InputResponse[];
  control: Control<FormValues>;
  register: UseFormRegister<FormValues>;
  errors: FieldErrors<FormValues>;
  getValues: UseFormGetValues<FormValues>;
  setValue: UseFormSetValue<FormValues>;
  addressPlaceholder?: string;
  isSameAsMineChecked?: boolean;
}

/**
 * Renders a full address input block: street autocomplete, optional unit type/number,
 * city, and state + zip side by side. Handles address suggestion parsing and zip code
 * lookup internally. Can be dropped into any form that uses `Record<string, string>` form values.
 */
const AddressFieldsBlock = ({
  inputs,
  control,
  register,
  errors,
  getValues,
  setValue,
  addressPlaceholder = "Enter address",
  isSameAsMineChecked,
}: AddressFieldsBlockProps) => {
  const fullAddressInput = inputs.find((i) => i.dataType === "fullAddress");
  const unitTypeInput    = inputs.find((i) => i.dataType === "addressUnitType");
  const unitIdentInput   = inputs.find((i) => i.dataType === "addressUnitIdentifier");
  const cityInput        = inputs.find((i) => i.dataType === "city");
  const stateInput       = inputs.find((i) => i.dataType === "state");
  const zipInput         = inputs.find((i) => i.dataType === "zipCode");

  const hasUnitFields = !!(unitTypeInput ?? unitIdentInput);
  const unitRowId = useId();
  const [showUnit, setShowUnit] = useState(() =>
    !!(
      unitTypeInput?.initialValue ||
      unitIdentInput?.initialValue ||
      (unitTypeInput && getValues(unitTypeInput.name)) ||
      (unitIdentInput && getValues(unitIdentInput.name))
    )
  );

  // ── Zip code lookup ──
  const [selectedPlaceId, setSelectedPlaceId] = useState<string | null>(null);
  const [zipAutofillResetKey, setZipAutofillResetKey] = useState(0);
  const { data: resolvedZip } = useGetAddressZipCodeQuery(selectedPlaceId!, {
    skip: !selectedPlaceId,
  });

  useEffect(() => {
    if (resolvedZip && selectedPlaceId && zipInput) {
      setValue(zipInput.name, resolvedZip.slice(0, 5), { shouldValidate: true });
      setSelectedPlaceId(null);
      setZipAutofillResetKey((key) => key + 1);
    }
  }, [resolvedZip, selectedPlaceId, zipInput, setValue]);

  // ── Address autocomplete selection ──
  const handleAddressPick = (data: AddressSuggestion) => {
    const parts = data.description
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);
    const len = parts.length;

    if (len >= 3) {
      const citySegment = len > 3 ? parts[len - 3] : parts[len - 2];
      const stateSegment = len > 3 ? parts[len - 2] : parts[len - 1];

      if (cityInput) {
        setValue(cityInput.name, citySegment, { shouldValidate: true });
      }
      if (stateInput && statesOfUSA.includes(stateSegment)) {
        setValue(stateInput.name, stateSegment, { shouldValidate: true });
      }
    }
    if (zipInput) {
      setSelectedPlaceId(data.placeId);
    }
  };

  return (
    <div className="address-fields-block">

      {/* 1. Street address autocomplete */}
      {fullAddressInput && (
        <Controller
          name={fullAddressInput.name}
          control={control}
          render={({ field }) => (
            <AutoSuggestion
              {...field}
              label={fullAddressInput.label}
              placeholder={addressPlaceholder}
              setOptionData={handleAddressPick}
              preFilledValue={getValues(fullAddressInput.name)}
              isSameAsMineChecked={isSameAsMineChecked}
              errorMessage={errors[fullAddressInput.name]?.message}
            />
          )}
        />
      )}

      {/* 2. Unit type toggle + fields */}
      {hasUnitFields && (
        <div className="address-fields-block__unit-section">
          <button
            type="button"
            className="address-fields-block__unit-toggle"
            aria-expanded={showUnit ? "true" : "false"}
            aria-controls={unitRowId}
            onClick={() => setShowUnit((v) => !v)}
          >
            <FontAwesomeIcon
              icon={showUnit ? solid("minus") : solid("plus")}
              className="address-fields-block__unit-toggle-icon"
              aria-hidden="true"
            />
            {showUnit ? "Hide Unit Type" : "Add Unit Type"}
          </button>
          {showUnit && (
            <div className="address-fields-block__unit-row" id={unitRowId}>
              {unitTypeInput && (
                <Controller
                  name={unitTypeInput.name}
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={unitTypeInput.properties?.options ?? []}
                      placeholder="Select"
                      label={unitTypeInput.label}
                      initialValue={getValues(unitTypeInput.name)}
                      className="address-select"
                      errorMessage={errors[unitTypeInput.name]?.message}
                    />
                  )}
                />
              )}
              {unitIdentInput && (
                <FormInput
                  dataType={unitIdentInput.dataType}
                  formHandle={register(unitIdentInput.name)}
                  inputProps={{
                    name: unitIdentInput.name,
                    label: unitIdentInput.label,
                    placeholder: `Enter ${unitIdentInput.label}`,
                    errorMessage: errors[unitIdentInput.name]?.message,
                  }}
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* 3. City */}
      {cityInput && (
        <FormInput
          dataType={cityInput.dataType}
          formHandle={register(cityInput.name)}
          inputProps={{
            name: cityInput.name,
            label: cityInput.label,
            placeholder: `Enter ${cityInput.label}`,
            errorMessage: errors[cityInput.name]?.message,
          }}
        />
      )}

      {/* 4+5. State and Zip side by side */}
      <div className="address-fields-block__state-zip-row">
        {stateInput && (
          <Controller
            name={stateInput.name}
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                options={sort(stateInput.properties?.options ?? [])}
                placeholder="Select"
                label={stateInput.label}
                initialValue={getValues(stateInput.name)}
                className="state-select"
                excludeFromTranslation
                errorMessage={errors[stateInput.name]?.message}
              />
            )}
          />
        )}
        {zipInput && (
          <FormInput
            dataType={zipInput.dataType}
            formHandle={register(zipInput.name)}
            inputProps={{
              name: zipInput.name,
              label: zipInput.label,
              placeholder: `Enter ${zipInput.label}`,
              maxLength: 5,
              errorMessage: errors[zipInput.name]?.message,
              zipAutofillResetKey,
            }}
          />
        )}
      </div>

    </div>
  );
};

export default AddressFieldsBlock;
